/*
  Recently viewed products, on product pages.

  Two jobs:

  1. Record the product this page is about. layout/theme.liquid loads this file
     on product templates with the product id on the script tag. Ids are kept in
     localStorage, newest first, without duplicates.

  2. Fill <recently-viewed-products> (sections/section-recently_viewed.liquid).
     When the section comes near the screen it asks the search route for all the
     remembered products in one request, rendered by
     sections/recently-viewed-cards.liquid, and shows the carousel only if enough
     products come back. Otherwise the section stays hidden.

  Standalone rather than part of global.js, which ships as a hand-edited
  minified pair.
*/
(function () {
  var STORAGE_KEY = 'wb:recently-viewed';
  var MAX_STORED = 20;

  function readIds() {
    try {
      var stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored.map(Number).filter(function (id) { return id > 0; });
    } catch (error) {
      return [];
    }
  }

  function writeIds(ids) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, MAX_STORED)));
    } catch (error) {
      /* Storage can be full or blocked; the page works without it. */
    }
  }

  var script = document.currentScript;
  var currentId = script ? Number(script.getAttribute('data-product-id')) : 0;
  if (currentId > 0) {
    writeIds([currentId].concat(readIds().filter(function (id) { return id !== currentId; })));
  }

  if (window.customElements.get('recently-viewed-products')) return;

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      /*
        The element is hidden until it has products, and a hidden element has no
        box to observe, so the observer watches its section wrapper instead.
      */
      var target = this.closest('.shopify-section') || this.parentElement;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        this.observer.disconnect();
        this.observer = null;
        this.load();
      }, { rootMargin: '0px 0px 600px 0px' });
      this.observer.observe(target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.abortController) this.abortController.abort();
    }

    async load() {
      var excludeId = Number(this.dataset.excludeId) || 0;
      var limit = Number(this.dataset.limit) || 10;
      var minimum = Number(this.dataset.minimum) || 3;
      var ids = readIds().filter(function (id) { return id !== excludeId; }).slice(0, limit);
      if (ids.length < minimum) return;

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var query = ids.map(function (id) { return 'id:' + id; }).join(' OR ');
      var url = root + 'search?type=product&q=' + encodeURIComponent(query) + '&section_id=recently-viewed-cards';

      var html;
      this.abortController = new AbortController();
      try {
        var response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) return;
        html = await response.text();
      } catch (error) {
        return;
      } finally {
        this.abortController = null;
      }

      var doc = new DOMParser().parseFromString(html, 'text/html');
      var cells = Array.from(doc.querySelectorAll('[data-recently-viewed-cards] > .product-carousel__cell'));
      if (cells.length < minimum) return;

      var template = this.querySelector('template[data-recently-viewed-template]');
      var container = this.querySelector('[data-recently-viewed-carousel]');
      if (!template || !container) return;

      var carousel = template.content.cloneNode(true);
      var slides = carousel.querySelector('[data-recently-viewed-slides]');
      cells.forEach(function (cell) { slides.appendChild(document.importNode(cell, true)); });

      /*
        Shown before the carousel goes in, so swiper-init measures a visible
        container and does not defer itself waiting for one.
      */
      this.hidden = false;
      container.appendChild(carousel);
      this.reveal();
    }

    /*
      Product cards carry .animatedContent, which is invisible until the theme's
      animation reveals it. The theme's own observer never sees cards added after
      load, so reveal them the way product-recommendations does, or show them
      plainly when animations are off.
    */
    reveal() {
      var targets = Array.from(this.querySelectorAll('.animatedContent'));
      if (!targets.length) return;
      var method = this.dataset.animation;
      if (window.theme && window.theme.enableAnimations && typeof RevolutionAnimation === 'function') {
        var animation = new RevolutionAnimation(this, 'true');
        if (method && typeof animation[method] === 'function') {
          animation[method](targets);
          return;
        }
      }
      targets.forEach(function (target) { target.classList.remove('animatedContent'); });
    }
  }

  window.customElements.define('recently-viewed-products', RecentlyViewedProducts);
})();
